import type { Pillar } from './pillars.js';
import { EXTRA_DESTINATIONS } from './navExtraDestinations.js';
import type { Role } from '@attendance/shared';
import type { FeatureFlags, FeatureKey } from '@attendance/shared/features';
import { useBrandingStore } from '../../stores/brandingStore.js';
/*
 * THE GLYPH SET.
 *
 * Every icon on the rail comes from lucide's OUTLINE set and, since the rail
 * renders them all through one shared size/weight (see `NAV_ICON` in
 * Sidebar.tsx), the only thing left that can make the set look accidental is
 * the drawing itself. Two rules hold here:
 *
 *   1. ONE FAMILY. Lucide ships people glyphs twice — a standard set (`Users`,
 *      `UserCheck`, `CircleUser`) and a "round" set (`UsersRound`,
 *      `CircleUserRound`, exported under the legacy `Users2`/`UserCircle2`
 *      names). They differ in head shape, so a rail that draws from both reads
 *      as two icon sets stacked. Everything here is the standard set.
 *   2. NO TWO DESTINATIONS THAT CAN APPEAR TOGETHER SHARE A GLYPH. Collapsed,
 *      the rail is nothing BUT glyphs — two rows drawn identically are two rows
 *      a user cannot tell apart, and the label that separated them is gone.
 *      Sidebar.test.tsx pins this by comparing the rendered path data, so it
 *      catches two different imports that turn out to be the same picture.
 *
 *      SECTION HEADINGS are outside the rule and repeat their anchor row's
 *      glyph on purpose — Workforce/Employees, Time & pay/Shifts. A heading is
 *      not somewhere you can go, it is the name of what is under it, and even
 *      collapsed it is set apart by the hairline the rail draws above it.
 */
import {
  ArrowLeftRight,
  BookUser,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CheckCheck,
  ClipboardCheck,
  CircleUser,
  Clock,
  FileCheck,
  FileCheck2,
  FileSignature,
  FileText,
  Gauge,
  History,
  House,
  IdCard,
  Landmark,
  MonitorSmartphone,
  MapPin,
  Megaphone,
  Palmtree,
  Radar,
  Search,
  ServerCog,
  Settings as SettingsIcon,
  Stamp,
  Table2,
  Tags,
  Truck,
  UserCheck,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

export { EXTRA_DESTINATIONS };

export interface NavLeaf {
  /** Overrides the group's pillar for a row that belongs to another area. */
  pillar?: Pillar;
  to: string;
  /** i18n key under `nav.*`. */
  labelKey: string;
  /** English fallback. */
  label: string;
  icon: LucideIcon;
  roles?: Role[];
  /**
   * Feature module(s) this item belongs to; hidden when the flag is off. An
   * array requires ALL listed features to be on.
   */
  feature?: FeatureKey | FeatureKey[];
  /**
   * Wording that replaces `labelKey`/`label` while a feature is OFF. This is
   * NOT a second gate: the item stays visible and reachable either way, only
   * its name changes. The one case is the site board, which is the "Site & camp
   * board" only while the camps pillar is on — with camps off the same page is
   * simply the site board, and a nav item named after a module the tenant does
   * not run is the same defect as a screen for it. Reads the same
   * `FeatureFlags` as `feature`, so there is one source of truth for both.
   */
  labelWithout?: { feature: FeatureKey; labelKey: string; label: string };
  /**
   * Hide this item when the tenant runs the construction terminology profile.
   * Used for management actions that don't fit login-less site workers — e.g.
   * the staff "Bulk import" (which needs emails) is wrong for construction,
   * where workers come from the CLI worker/site-master import instead. There's
   * no "import" feature flag, so this gates on the profile directly.
   */
  hideInConstruction?: boolean;
  /**
   * Count rendered as a pill on this row. The rail is where an admin learns
   * there is work waiting — a review queue nobody looks at is a queue that
   * keeps people locked out for a day. Named rather than a number so the nav
   * table stays static data: the Sidebar mounts the matching hook only for the
   * row that asks for one, instead of every leaf running a query.
   *
   * All three approval queues carry one, because they are one kind of thing —
   * rows of people waiting on a decision — and the rail was previously the
   * only surface in the app that counted one of them and not the other two.
   */
  badge?: NavBadgeKind;
  /**
   * A LIVE figure on this row: a number that describes the world right now
   * rather than a queue, drawn with the app's one permitted animation.
   *
   * Declared here rather than special-cased in `Sidebar.tsx` for the reason §2
   * gives for the rest of this file — rail contents come from the nav model, and
   * a number the rail states is rail content. Named, like `badge`, so the query
   * behind it mounts for one row instead of forty.
   */
  live?: NavLiveKind;
}

/**
 * The counted queues. Each one names a hook in `NavBadge.tsx`, and each of
 * those hooks reads the query key the QUEUE'S OWN SCREEN reads — so a badge
 * counts the rows its queue shows rather than a second server-side count with
 * its own idea of scope (rule 3.6).
 */
export type NavBadgeKind =
  | 'correctionApprovals'
  | 'leaveApprovals'
  | 'letterApprovals'
  | 'deviceApprovals'
  | 'erpChanges';

/** The live figures. One, so far: how many people are on a worksite now. */
export type NavLiveKind = 'onSiteNow';

export interface NavGroup {
  id: string;
  titleKey: string;
  title: string;
  icon: LucideIcon;
  items: NavLeaf[];
  /** Which pillar accent this group's rows carry. See `Pillar` below. */
  pillar?: Pillar;
}


/**
 * The single most important action — elevated as a primary CTA everywhere.
 *
 * It lands on HOME, not on a separate page: the check-in panel is the top of
 * the personal dashboard now, so the standalone /punch route is gone. The CTA
 * stays because "check in" is what people are looking for in the rail — naming
 * that destination "Home" would hide the app's main verb behind a place.
 */
export const PRIMARY: NavLeaf = {
  to: '/',
  labelKey: 'nav.punch',
  label: 'Check in / out',
  icon: Stamp,
  // Personal self-punch. Hidden when the tenant is a management-only console
  // (login-less site workers punch via the field APK/kiosk instead).
  feature: 'selfService',
};

/**
 * "Me" — the personal workspace every user (admins included) lands on. Home is
 * the personal dashboard and is always shown; the rest are self-service items.
 * Corrections lives here too: it's the worker's own "request a fix to my
 * punches" page (the approver's queue is the same route, role-adapted), so it
 * belongs to the personal side, not the Admin panel.
 * Notifications lives only in the top-bar bell (and the mobile "Alerts" tab).
 */
export const PERSONAL: NavLeaf[] = [
  // `House`, not `LayoutDashboard`. This row is labelled "Home" and it lands on
  // the worker's own screen; the grid glyph is what the app draws for a
  // dashboard, so the picture and the word disagreed. It is also the drawing the
  // mobile bottom bar's home tab already uses — lucide's `Home` is an alias of
  // `House`, so the two surfaces are now one picture rather than two.
  { to: '/', labelKey: 'nav.home', label: 'Home', icon: House },
  { to: '/my-attendance', labelKey: 'nav.myAttendance', label: 'My attendance', icon: Clock, feature: 'selfService' },
  { to: '/corrections', labelKey: 'nav.corrections', label: 'Corrections', icon: FileSignature, feature: 'corrections' },
  { to: '/leave', labelKey: 'nav.myLeave', label: 'My leave', icon: CalendarDays, feature: 'leave' },
  { to: '/announcements', labelKey: 'nav.announcements', label: 'Announcements', icon: Megaphone, feature: 'announcements' },
  // A letter is a document, not a workbook — this row carried `FileSpreadsheet`,
  // which is the glyph the app uses for an Excel export everywhere else.
  { to: '/letters', labelKey: 'nav.letters', label: 'Letters', icon: FileText, feature: 'letters' },
  { to: '/profile', labelKey: 'nav.myProfile', label: 'My profile', icon: CircleUser, feature: 'selfService' },
];

/**
 * The heading over the personal list — the one section title on this side of
 * the rail.
 *
 * The admin surface names every one of its sections and the personal surface
 * named none, so on a COMBINED console (self-service off is not the only way to
 * get one — see `mode` in Sidebar.tsx) the personal rows and the first admin
 * group ran together under a single hairline with nothing saying which was
 * which. The handoff draws a heading here for that reason.
 *
 * IT REUSES `nav.myWorkspace`, and that is the whole point of not adding a key:
 * "My workspace" is already the eyebrow every personal PAGE opens with
 * (MyAttendancePage, MyLeavePage, LettersPage, AnnouncementsPage…) and already
 * the words on the switch back from the Admin panel. A rail heading with its
 * own wording for the same surface would be the app calling one place two
 * things. The handoff's own label is "Me"; ours is the vocabulary the product
 * already ships.
 *
 * Items are NOT held here: the personal list is filtered per user (feature
 * flags, and whether the login has an Employee record behind it at all), so
 * this carries only the naming. `Omit<NavGroup, 'items'>` rather than a fourth
 * shape, so a heading is the same kind of thing on both surfaces.
 *
 * `CircleUser` repeats the "My profile" row's glyph, under the heading rule at
 * the top of this file — a heading is the name of what is under it, not a
 * destination, so it is outside the one-picture-one-destination rule.
 */
export const PERSONAL_SECTION: Omit<NavGroup, 'items'> = {
  id: 'me',
  titleKey: 'nav.myWorkspace',
  title: 'Me',
  icon: CircleUser,
};

/**
 * Landing page of the Admin panel (the management surface). Everyone — admins
 * included — lands on their personal workspace by default; opening the Admin
 * panel brings them here, the workforce/management overview. It sits above the
 * back-office GROUPS in the admin sidebar.
 */
export const ADMIN_HOME = '/overview';

export const OVERVIEW: NavLeaf = {
  /* The admin home takes the BRAND, which is also the resolver's fallback — so
     this tag changes nothing visually and everything about the contract: an
     untagged top-level destination is a silent regression to "everything is
     brand", and `pillarAccent.test.tsx` can only catch that if every area
     states its pillar rather than relying on the default. */
  pillar: 'people',
  to: ADMIN_HOME,
  labelKey: 'nav.overview',
  label: 'Overview',
  icon: Gauge,
  roles: ['ADMIN', 'HR', 'MANAGER'],
};

/**
 * The control room — today's floor, in one screen.
 *
 * It used to be a GROUP of three: "Live attendance", "Site & camp board" and
 * "Live site board". They answered overlapping halves of one question and
 * disagreed in the gaps, so they are now one page with four views, which leaves
 * the group holding a single item named the same thing as the group it sits in
 * — the exact defect the Reports note below describes. So it is promoted to a
 * top-level management destination beside Overview instead: two rows, the
 * summary and the floor, then the back-office sections underneath.
 *
 * NOT feature-gated. The site half needs the `workers` pillar and the page
 * hides it accordingly, but the people half (who is on the clock, who is
 * flagged) applies to every tenant — and one of the pages this replaces was
 * reachable with no feature gate at all.
 */
export const OPERATIONS: NavLeaf = {
  pillar: 'sites',
  to: '/operations',
  labelKey: 'nav.operations',
  label: 'Operations',
  icon: Radar,
  roles: ['ADMIN', 'HR', 'MANAGER'],
  /*
   * The floor, as one number, without opening the floor.
   *
   * It is the same figure as this page's own "On site now" tile — same feed,
   * same sum — because a rail that quietly disagreed with the screen it links
   * to would be the harder half of the pair to catch (rule 3.2). Gated inside
   * `NavLiveCount` on the `workers` pillar: the feed is per-worksite, and a
   * tenant with no sites gets no figure rather than a zero.
   */
  live: 'onSiteNow',
};

/**
 * The month, for the whole workforce. Promoted for the same reason as
 * Operations: it is a screen you open to look at the company, it owns its own
 * filters (group, department, month), and it is the one management destination
 * a tenant with no leave, shifts or payroll module still has — under the lean
 * preset it was the ONLY survivor of its group, i.e. a collapsible section you
 * expanded to find one link.
 *
 * Not feature-gated, like Operations: attendance is what this app is.
 */
export const ATTENDANCE_CALENDAR: NavLeaf = {
  pillar: 'attendance',
  to: '/attendance-calendar',
  labelKey: 'nav.attendanceCalendar',
  label: 'Attendance calendar',
  icon: CalendarCheck,
  roles: ['ADMIN', 'HR', 'MANAGER'],
};

/**
 * REPORTS IS ONE DESTINATION, at the top level, beside the other three.
 *
 * It has been a leaf, then a two-row section, and it is a leaf again — so the
 * reason matters more than usual. A sidebar section earns its heading by
 * holding rows a reader has to CHOOSE BETWEEN. Reports stopped being that when
 * the section collapsed onto one screen: `/reports` is a search bar over the
 * tenant's own figures, every report is a question it composes, and the two
 * that are still screens (the attendance workspace, the monthly timesheet) are
 * offered inside it by name — through the same catalog that gates them on role
 * and feature, so the bar cannot offer what the rail would have hidden.
 *
 * That left a heading, a chevron, and a row called "Smart report" nested under
 * a heading called "Reports" — the reader paying a click to be told the section
 * is the screen. And "Smart report" named the CONTROL rather than the
 * destination: nobody in the office goes looking for a smart report, they go
 * looking for reports.
 *
 * It sits at the top level rather than in a section because it is the same
 * shape as the three beside it — a screen you open to look at the company, that
 * owns its own controls and its own way in. Overview, Operations, Attendance
 * calendar, Reports: the summary, today's floor, the month, and the question.
 *
 * `Search`, not `Sparkles`. There is no model in it and nothing is generated; a
 * magic glyph would promise one. Same reason the Administration group rejected
 * the same icon.
 */
export const REPORTS: NavLeaf = {
  pillar: 'reports',
  to: '/reports',
  labelKey: 'nav.reports',
  label: 'Reports',
  icon: Search,
  roles: ['ADMIN', 'HR', 'MANAGER'],
  feature: 'reports',
};

/**
 * THE ATTENDANCE WORKSPACE, BACK ON THE RAIL.
 *
 * It had no row of its own, on the reasoning recorded above: Reports offers it
 * by name inside the bar, so a rail row would be a second way in to something
 * already listed one row up. That reasoning holds for a reader who knows the
 * screen exists and knows the bar will offer it. It fails for everyone else,
 * and it failed in practice — the workspace is where the office actually
 * works (filter once; KPIs, charts and one table of hours and overtime you can
 * sort, narrow and export), and taking its row away made a daily destination
 * something you had to remember the name of before the app would admit to it.
 *
 * Findability beats tidiness here. `/reports` answers a QUESTION and hands back
 * one figure with its working; this hands back the whole table. They are two
 * different jobs, which is exactly the test a rail row is supposed to pass —
 * rows the reader has to choose between — so both get one.
 *
 * `Table2`, not another magnifier: the difference between the two rows IS that
 * one is a search and the other is a table, and the glyphs should say so at a
 * glance rather than making the label do all the work.
 */
export const REPORTS_WORKSPACE: NavLeaf = {
  pillar: 'reports',
  to: '/reports/workspace',
  labelKey: 'nav.reportsWorkspace',
  label: 'Attendance workspace',
  icon: Table2,
  roles: ['ADMIN', 'HR', 'MANAGER'],
  feature: 'reports',
};


/**
 * The management destinations that stand on their own, in the order the office
 * reaches for them: the summary, today's floor, the month, the question.
 *
 * Each one owns an internal navigator — Operations has four views, the
 * attendance calendar its filters, Reports its search bar — which is precisely
 * why none of them belongs inside a collapsible list of siblings. What they
 * have in common is the test for this list: you open it to look at the company,
 * not to act on one record.
 */
export const TOP_LEVEL: NavLeaf[] = [
  OVERVIEW,
  OPERATIONS,
  ATTENDANCE_CALENDAR,
  REPORTS,
  REPORTS_WORKSPACE,
];

/**
 * Management-console back-office — collapsible, role + feature filtered, and
 * ordered by how often the office actually opens each one:
 *   Approvals (somebody is waiting) → Workforce (people records) →
 *   Time & pay (planning + pay) → Administration (set-up, last).
 *
 * FOUR GROUPS, not eight, and the count is the discipline. The old Approvals /
 * Leave / Time / Payroll groups each held one or two rows after role and
 * feature filtering, so a manager expanded four near-empty sections to find one
 * link; Reports was two rows under a heading of the same name. A section earns
 * its heading by holding rows the reader has to choose between — anything that
 * cannot is a top-level destination (Overview, Operations, the calendar,
 * Reports) or a child of the page it belongs to.
 *
 * The dividing line between here and TOP_LEVEL: these groups are where you go
 * to ACT on records and to configure the tenant. Anything a tenant configures
 * once — reference lists, system tooling, shift definitions — sits in
 * Administration rather than competing with daily work.
 */
export const GROUPS: NavGroup[] = [
  {
    /*
     * THE QUEUES COME FIRST — the only rows here that are somebody waiting.
     *
     * The merged Time & pay note said "action queues (Corrections, Leave
     * approvals) lead", but Corrections was never in the admin surface at all
     * (it lives in the personal list, which admin mode does not render), so the
     * group's first row was Leave approvals and the app's second approval queue
     * could not be reached from the Admin panel.
     */
    id: 'approvals',
    pillar: 'attendance',
    titleKey: 'nav.groups.approvals',
    title: 'Approvals',
    // `Sparkles` is lucide's AI/magic glyph. It said nothing about a queue of
    // people waiting on a decision, and it was drawn on BOTH the section header
    // and one of the rows inside it — so the section and its child were the
    // same picture. A check is what the rows here have in common.
    icon: CheckCheck,
    items: [
      // The approver's queue, not the worker's own request form: `FileSignature`
      // (sign a document) belongs to /corrections in the personal list, which
      // renders alongside this row on a combined-surface console.
      //
      // "Correction APPROVALS", not "Correction requests". Under a heading that
      // says Approvals, three rows read "<thing> approvals" and this one read
      // "<thing> requests" — the worker's word for the object, on the approver's
      // row. One shape for the four (§3.6), and the shape is the one that says
      // what the reader came here to do.
      { to: '/corrections/approvals', labelKey: 'nav.correctionApprovals', label: 'Correction approvals', icon: FileCheck, roles: ['ADMIN', 'HR', 'MANAGER'], feature: 'corrections', badge: 'correctionApprovals' },
      // THE QUEUE HALF OF THE LEAVE PAGE. /leave/manage and /leave/calendar are
      // two addresses of one screen now (see the header of LeaveManagePage.tsx),
      // and this row names the one an approver comes for: it opens on the queue,
      // which is what the badge beside it counts. Two rows for one page is
      // usually the defect the device merge removed — it is right here because
      // each view has its OWN PATH, so each row highlights on its own view and
      // only there, which a `?view=` row could never do.
      { to: '/leave/manage', labelKey: 'nav.leaveApprovals', label: 'Leave approvals', icon: ClipboardCheck, roles: ['ADMIN', 'HR', 'MANAGER'], feature: 'leave', badge: 'leaveApprovals' },
      // THE FOURTH QUEUE, and until now the only one with no door on this
      // surface. A letter request is decided by the same three roles that
      // decide the two rows above, and the queue existed — `LetterApprovalsPanel`
      // behind `?tab=queue`, counted by the dashboard's own decision card — but
      // `/letters` is a PERSONAL row, and admin mode renders no personal rows.
      // So an approver working the Admin panel could reach three of the app's
      // four queues.
      //
      // It points at `/letters/approvals` rather than `?tab=queue` for the
      // reason the Devices note below spells out: `pathMatches` compares `to`
      // against `pathname` with no query awareness, so a query-string row could
      // never highlight. Corrections solved this the same way and this is that
      // solution applied, not a new pattern.
      { to: '/letters/approvals', labelKey: 'nav.letterApprovals', label: 'Letter approvals', icon: FileCheck2, roles: ['ADMIN', 'HR', 'MANAGER'], feature: 'letters', badge: 'letterApprovals' },
      // THE FIFTH QUEUE, and the one whose absence was worst. The ERP exchange
      // ran BOTH WAYS with nobody in it: a scheduled job wrote the file that
      // pays about a thousand people, and a nightly job rewrote payroll routing
      // from whatever workbook was in a folder. Both now wait on a person, and
      // this is where that person looks.
      //
      // ADMIN/HR, unlike the four rows above it. Leave, corrections and letters
      // are decided by whoever has authority over a WORKER, which is what makes
      // a MANAGER an approver there. Neither ERP object belongs to a team: one
      // is the whole workforce's month, the other is how everybody is paid.
      //
      // Points at the OUTBOUND half, because that is the one with a deadline —
      // the export fires on a configured day and withholds an unapproved batch.
      // An inbound change waits patiently; a payroll file does not.
      { to: '/erp/approvals', labelKey: 'nav.erpApprovals', label: 'ERP approvals', icon: ArrowLeftRight, roles: ['ADMIN', 'HR'], badge: 'erpChanges' },
      // THE DEVICE QUEUE, back under Approvals — every queue under one
      // heading, matching the handoff. It used to be filed under
      // Administration on the reasoning that a rail row names its
      // DESTINATION rather than what's inside it, and the destination is a
      // register that CONTAINS a queue rather than being one. Restored here
      // on the owner's call that "where an approver looks for waiting work"
      // outweighs that distinction — Correction requests and Leave approvals
      // are exactly this shape already (a queue view of a bigger page), so
      // Devices being the same shape is consistent, not an exception.
      //
      // This MOVES the one existing row, it does not add a second one: there
      // is still exactly one `/settings/devices` destination and one row
      // pointing at it, just filed here instead of under Administration. Two
      // rows at that path was the thing ruled out — `pathMatches` compares
      // `to` against `pathname` with no query awareness, so a second row at
      // the same bare path would highlight alongside this one on every visit,
      // and a `?status=PENDING` variant would never highlight at all.
      // NAMED FOR THE QUEUE, like its two siblings. The handoff calls this row
      // "Device approvals" (`Hadir Dashboard.dc.html:95`), and under an
      // Approvals heading that is what an approver is looking for. It differs
      // from the page's own "Devices" heading — but so do the two rows above
      // it ("Correction requests" opens *Corrections*, "Leave approvals" opens
      // *Leave*), because in every case the rail names the QUEUE and the page
      // names the REGISTER that contains it. The page keeps "Devices" because
      // it is also a Settings destination, where the register is the point.
      { to: '/settings/devices', labelKey: 'nav.devices', label: 'Device approvals', icon: MonitorSmartphone, roles: ['ADMIN', 'HR'], badge: 'deviceApprovals' },
    ],
  },
  {
    id: 'workforce',
    pillar: 'people',
    titleKey: 'nav.groups.workforce',
    title: 'Workforce',
    icon: Users,
    items: [
      { to: '/employees', labelKey: 'nav.employees', label: 'Employees', icon: Users, roles: ['ADMIN', 'HR', 'MANAGER'] },
      // BULK IMPORT IS NOT HERE ANY MORE — it is a child of the row above, and
      // the rail was drawing it as that row's sibling. `/employees/import` is
      // reached from the Employees page's own header action, which is where a
      // reader is standing when they decide to load a spreadsheet of people:
      // nobody opens the sidebar to import, they open the list, see it is
      // empty, and import. Listing it here made the flat rail claim a
      // hierarchy the routes contradict, and put a once-a-quarter task beside
      // the app's most-opened screen. It stays in EXTRA_DESTINATIONS so ⌘K
      // still jumps straight to it.
      { to: '/worksites', labelKey: 'nav.worksites', label: 'Worksites', icon: MapPin, roles: ['ADMIN', 'HR', 'MANAGER'], feature: 'workers' },
      // These two were adjacent rows drawn with the SAME glyph — legible only
      // because the labels were there to tell them apart, which is precisely
      // what the collapsed rail takes away. A directory is a book of people; a
      // team's attendance is a person with a tick.
      { to: '/team', labelKey: 'nav.teamDirectory', label: 'Team directory', icon: BookUser, roles: ['ADMIN', 'HR', 'MANAGER'], feature: 'team' },
      { to: '/team-attendance', labelKey: 'nav.teamAttendance', label: 'Team attendance', icon: UserCheck, roles: ['ADMIN', 'HR', 'MANAGER'], feature: 'team' },
      /* SCHEDULE AND PAYROLL PREP MOVED HERE, and "Time & pay" is gone.
         That section held three rows: the leave calendar, the schedule and
         payroll prep. The calendar row is retired — it is one VIEW of the leave
         approvals screen, which carries a two-tab strip, so the row was a
         second address for a tab. What was left could not stand up: for a
         MANAGER payroll is ADMIN/HR-only, so the section became a heading, a
         chevron and one link — the exact decay `navItems.test.ts` guards, and
         the reason the calendar row had been kept until now.

         So the SECTION went instead of the row. Both survivors are about the
         subject this group already holds: who your people are, where they work,
         WHEN they work, and what they are paid. Three groups now, and the count
         is still the discipline — a section earns its heading by holding rows
         the reader has to choose between. */
      { to: '/schedule', labelKey: 'nav.schedule', label: 'Schedule', icon: CalendarClock, roles: ['ADMIN', 'HR', 'MANAGER'], feature: 'shifts' },
      { to: '/payroll', labelKey: 'nav.payrollPrep', label: 'Payroll prep', icon: Wallet, roles: ['ADMIN', 'HR'], feature: 'payroll' },
    ],
  },
  {
    // Everything you configure once and rarely touch: settings, the reference
    // lists (departments/designations/leave types/holidays) and system tooling.
    // Deliberately LAST.
    //
    // It used to claim "and collapsed by default", which stopped being true when
    // the Sidebar moved every group to open-by-default (see the note there —
    // three of the app's commonest tasks were paying a click to reveal a
    // heading's children). A comment describing behaviour the code no longer has
    // is the same defect as a screen describing data it no longer holds.
    id: 'administration',
    pillar: 'admin',
    titleKey: 'nav.groups.administration',
    title: 'Administration',
    // The COG, not the wrench. A section heading is the name of what is under
    // it, and this section's anchor row is Settings — the same relationship
    // Workforce has to Employees and Time & pay to Shifts. The wrench said
    // "repairs", which is not what a tenant comes here to do.
    icon: SettingsIcon,
    items: [
      { to: '/settings', labelKey: 'nav.settings', label: 'Settings', icon: SettingsIcon, roles: ['ADMIN', 'HR'] },
      { to: '/departments', labelKey: 'nav.departments', label: 'Departments', icon: Building2, roles: ['ADMIN', 'HR'], feature: 'orgStructure' },
      { to: '/designations', labelKey: 'nav.designations', label: 'Designations', icon: IdCard, roles: ['ADMIN', 'HR'], feature: 'orgStructure' },
      { to: '/branches', labelKey: 'nav.branches', label: 'Company branches', icon: Landmark, roles: ['ADMIN', 'HR'] },
      { to: '/suppliers', labelKey: 'nav.suppliers', label: 'Labour suppliers', icon: Truck, roles: ['ADMIN', 'HR'] },
      { to: '/leave/types', labelKey: 'nav.leaveTypes', label: 'Leave types', icon: Tags, roles: ['ADMIN', 'HR'], feature: 'leave' },
      // The shift DEFINITIONS, beside the other reference lists — see the note
      // where this row used to sit, under Time & pay.
      { to: '/shifts', labelKey: 'nav.shifts', label: 'Shifts', icon: Clock, roles: ['ADMIN', 'HR'], feature: 'shifts' },
      { to: '/holidays', labelKey: 'nav.holidays', label: 'Holidays', icon: Palmtree, roles: ['ADMIN', 'HR'], feature: 'leave' },
      // THE DEVICE REGISTER ROW MOVED TO APPROVALS (see the note there) — this
      // group no longer names it. `MonitorSmartphone` and the pending badge
      // both travelled with it; there is still exactly one row and one
      // `/settings/devices` destination, only its section changed.
      { to: '/system/audit-log', labelKey: 'nav.auditLog', label: 'Audit log', icon: History, roles: ['ADMIN'] },
      { to: '/system/jobs', labelKey: 'nav.jobs', label: 'Jobs', icon: ServerCog, roles: ['ADMIN'] },
    ],
  },
];

/**
 * Routes that take the FULL width of the shell instead of the reading cap.
 *
 * The cap (`max-w-content`) is right for a form, a table and a settings page,
 * and wrong for a wall display: a map capped at the text measure with a metre
 * of background either side of it is not a control room. Declared here, beside
 * the rest of the route knowledge, so the exception lives in one place rather
 * than each page fighting the wrapper with negative margins.
 *
 * THE ATTENDANCE CALENDAR IS THE SECOND MEMBER, and for the same reason rather
 * than a new one. Its grid is one column PER DAY — up to 31 of them beside the
 * employee column and the P/A total — so its natural width is a function of the
 * month, not of a reading measure. Capped at 1600px the day columns compress
 * until the cells stop being scannable, which is the whole job of the screen;
 * the cap does not make it easier to read, it makes it narrower. Everything
 * else about the page (its measure, its cards, its copy) still answers to the
 * ordinary rules.
 */
const WIDE_ROUTES: readonly string[] = [OPERATIONS.to, ATTENDANCE_CALENDAR.to];

export function isWideRoute(pathname: string): boolean {
  return WIDE_ROUTES.some((to) => pathMatches(to, pathname));
}

/** Role gate for a single nav item. */
export function canSee(item: { roles?: Role[] }, role: Role | undefined): boolean {
  return !item.roles || (!!role && item.roles.includes(role));
}

/** Feature gate for a single nav item. Untagged items are always on; a tagged
 * item shows only when its flag is not explicitly off (fail-open on absent). */
export function featureOn(
  item: { feature?: FeatureKey | FeatureKey[] },
  features: FeatureFlags | undefined
): boolean {
  if (!item.feature || !features) return true;
  const keys = Array.isArray(item.feature) ? item.feature : [item.feature];
  return keys.every((k) => features[k] !== false);
}

/**
 * The wording to render for an item, given the tenant's flags — `labelWithout`
 * when its named feature is explicitly off, the item's own label otherwise.
 * Fail-open on an absent flag set, exactly like `featureOn`.
 */
export function navLabelOf(
  item: NavLeaf,
  features: FeatureFlags | undefined
): { labelKey: string; label: string } {
  const alt = item.labelWithout;
  if (alt && features && features[alt.feature] === false) {
    return { labelKey: alt.labelKey, label: alt.label };
  }
  return { labelKey: item.labelKey, label: item.label };
}

/**
 * True when the tenant is running the construction terminology profile
 * (login-less site workers, Sites & Camps). Read straight from the branding
 * store so the existing `isVisible`/`allDestinations` call sites (Sidebar,
 * CommandPalette) pick up the profile gate without a signature change. The
 * Sidebar subscribes to `branding`, so it re-renders — and re-runs this
 * filter — whenever branding resolves or changes.
 */
export function isConstructionProfile(): boolean {
  return useBrandingStore.getState().branding?.terminology?.profile === 'construction';
}

/** Profile gate for a single nav item. Items not tagged are always allowed. */
export function profileOn(item: { hideInConstruction?: boolean }): boolean {
  return !item.hideInConstruction || !isConstructionProfile();
}

/** Combined role + feature + profile visibility for a single nav item. */
export function isVisible(
  item: NavLeaf,
  role: Role | undefined,
  features: FeatureFlags | undefined
): boolean {
  return canSee(item, role) && featureOn(item, features) && profileOn(item);
}

/** Active for `/` only on exact match; otherwise on the path or a sub-path. */
export function pathMatches(to: string, pathname: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * Does this user have ANY management access — i.e. is there at least one
 * back-office item their role + features let them see? Drives whether the
 * "Admin panel" switch appears at all. A rank-and-file employee gets `false`
 * and never sees the management surface.
 */
export function hasAdminAccess(
  role: Role | undefined,
  features: FeatureFlags | undefined
): boolean {
  // Only role-restricted (management) items count. An ungated back-office item
  // would otherwise grant every employee "admin access"; such items belong to
  // the personal workspace instead.
  return (
    TOP_LEVEL.some((i) => !!i.roles && isVisible(i, role, features)) ||
    GROUPS.some((g) => g.items.some((i) => !!i.roles && isVisible(i, role, features)))
  );
}

/**
 * Is `pathname` part of the Admin panel (management) surface? True for the
 * Overview landing and any back-office group route (incl. sub-paths). Personal
 * routes — home, punch, my-attendance, my leave, announcements, letters,
 * profile, notifications — are NOT admin paths, so they read as the personal
 * workspace. This is the single source of truth for which surface the shell is
 * showing, derived straight from the nav config so the two never drift.
 */
export function isAdminPath(pathname: string): boolean {
  // Role-gated items only — an ungated back-office route (e.g. Corrections) is
  // part of the personal workspace, so visiting it must NOT flip to admin mode.
  // Reading TOP_LEVEL rather than naming Overview and Operations by hand is
  // what keeps a newly promoted destination from silently dropping the reader
  // into the personal workspace on a back-office screen.
  const claims = (i: NavLeaf): boolean => !!i.roles && pathMatches(i.to, pathname);
  // EXTRA_DESTINATIONS COUNTS TOO, and leaving it out was a latent bug rather
  // than a simplification: a role-gated page is an admin page whether or not it
  // earned a rail row, and this list is exactly the pages that are reachable
  // without one. It went unnoticed while every entry was either ungated
  // (Notifications) or a query-string view of a listed row (the people map,
  // whose bare path `/operations` is claimed by TOP_LEVEL anyway). The leave
  // calendar is the first entry that is a real, role-gated path of its own —
  // without this line, an admin who opened it would have been dropped into the
  // personal workspace on a back-office screen.
  return (
    TOP_LEVEL.some(claims) ||
    GROUPS.some((g) => g.items.some(claims)) ||
    EXTRA_DESTINATIONS.some(claims)
  );
}

/** Every destination the role can reach, flattened — for the command palette. */
export function allDestinations(
  role: Role | undefined,
  features?: FeatureFlags
): NavLeaf[] {
  const primary = featureOn(PRIMARY, features) ? [PRIMARY] : [];
  const personal = PERSONAL.filter((i) => featureOn(i, features));
  const top = TOP_LEVEL.filter((i) => isVisible(i, role, features));
  const groupItems = GROUPS.flatMap((g) => g.items).filter((i) => isVisible(i, role, features));
  const extras = EXTRA_DESTINATIONS.filter((i) => isVisible(i, role, features));
  /*
   * DE-DUPED BY DESTINATION.
   *
   * The check-in CTA and the personal "Home" row are both `/` — the sidebar
   * knows that and drops one (it renders the CTA instead), but this list did
   * not, so the command palette offered two rows that went to the same screen
   * and React saw a duplicate key, because the palette keys on `to`.
   *
   * Keeping the FIRST occurrence keeps the CTA's wording ("Check in / out"),
   * which is what somebody typing into the palette is actually looking for —
   * naming that destination "Home" would hide the app's main verb behind a
   * place.
   */
  const seen = new Set<string>();
  return [...primary, ...personal, ...top, ...groupItems, ...extras].filter((i) => {
    if (seen.has(i.to)) return false;
    seen.add(i.to);
    return true;
  });
}
